/**
 * The force that pushes nodes apart in the layout: every pair of nodes closer than
 * {@link REACH} pushes itself apart, by `2600 / distance²`, and much harder when the two
 * overlap.
 *
 * Comparing every node with every other one costs n² / 2 pairs: 8 million at 4000 nodes, on
 * every frame. So this uses the **Barnes–Hut** method: the nodes are sorted into a tree of boxes
 * (each box split in 8, or 4 in the plane, until it holds a few nodes). A box that is far away
 * compared to its size pushes like one node standing at its centre of mass, with the weight of
 * all its nodes; near boxes are opened and their nodes met one by one. That costs about
 * n · log n. Only far, weak pushes are approximated: a box is taken whole only when it is far
 * enough that none of its nodes can overlap the node being pushed, so the overlap force stays
 * exact. A box partly beyond {@link REACH} counts whole too when its centre of mass is within
 * reach (its farthest nodes then push a little, about 1/100 of what a neighbour does, while its
 * nearest ones are counted at the centre's distance), which spares opening every box along
 * the edge of the reach.
 *
 * `spacing` stretches the whole layout: the reach grows with it and the push with its square,
 * so nodes settle about `spacing` times further apart (the overlap push, which depends on the
 * nodes' sizes, does not change).
 */

/** A node, as the layout moves it. */
export interface Body {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Its radius: nodes closer than both radii plus a margin are pushed harder. */
  r: number;
}

/** Beyond this distance (at spacing 1) two nodes do not push each other. */
export const REACH = 500;

/** A box is taken whole when its size is less than this share of its distance. */
const THETA = 0.7;

/** A box with this many nodes or fewer is not split. */
const LEAF = 12;

/** Boxes are not split deeper than this (many nodes at the same spot stay in one box). */
const MAX_DEPTH = 24;

/** One box of the tree. */
interface Cell {
  /** Its centre and half width (the same on every axis). */
  cx: number;
  cy: number;
  cz: number;
  half: number;
  /** How many nodes it holds, and their centre of mass. */
  mass: number;
  mx: number;
  my: number;
  mz: number;
  /** The boxes it is split into (`null` for a leaf). */
  children: Cell[] | null;
  /** A leaf's nodes, by index (`null` when split). */
  items: number[] | null;
}

/** How hard two nodes push each other at distance 1 (at spacing 1): the push is this / distance². */
const STRENGTH = 2600;

/** Where the bodies are, as numbers: what {@link pushes} reads (and a worker receives). */
export interface Places {
  /** Positions. */
  x: Float64Array;
  /** Positions. */
  y: Float64Array;
  /** Positions (all 0 on the plane). */
  z: Float64Array;
  /** Radii. */
  r: Float64Array;
}

/** The push on each body, as numbers (not yet scaled by the layout's energy). */
export interface Pushes {
  /** Push along x. */
  fx: Float64Array;
  /** Push along y. */
  fy: Float64Array;
  /** Push along z. */
  fz: Float64Array;
}

/** The places of `bodies` as numbers (z as 0 unless `deep`). */
export function placesOf(bodies: readonly Body[], deep: boolean): Places {
  const n = bodies.length;
  const places: Places = { x: new Float64Array(n), y: new Float64Array(n), z: new Float64Array(n), r: new Float64Array(n) };
  for (let i = 0; i < n; i++) {
    const b = bodies[i]!;
    places.x[i] = b.x;
    places.y[i] = b.y;
    places.z[i] = deep ? b.z : 0;
    places.r[i] = b.r;
  }
  return places;
}

/**
 * Adds to each body's speed the push of the other bodies within {@link REACH} × `spacing`,
 * scaled by `alpha` (the layout's energy). `deep`: also along z (the 3D view).
 */
export function repel(bodies: readonly Body[], alpha: number, deep: boolean, spacing = 1): void {
  applyPushes(bodies, pushes(placesOf(bodies, deep), deep, spacing), alpha);
}

/** Adds `found` (from {@link pushes}, for these bodies in this order), times `alpha`, to the bodies' speeds. */
export function applyPushes(bodies: readonly Body[], found: Pushes, alpha: number): void {
  const n = Math.min(bodies.length, found.fx.length);
  for (let i = 0; i < n; i++) {
    const b = bodies[i]!;
    b.vx += found.fx[i]! * alpha;
    b.vy += found.fy[i]! * alpha;
    b.vz += found.fz[i]! * alpha;
  }
}

/**
 * The push on the bodies `from` to `to` (excluded; default all of them) at `places`, from all
 * the others within {@link REACH} × `spacing`: the work itself, on plain numbers, so it can run
 * in workers, each taking a share of the bodies (see `repulsionWorker.ts`). The answer holds
 * one push per body asked about, in order.
 */
export function pushes(places: Places, deep: boolean, spacing = 1, from = 0, to = places.x.length): Pushes {
  const { x, y, z, r } = places;
  const n = x.length;
  const found: Pushes = { fx: new Float64Array(to - from), fy: new Float64Array(to - from), fz: new Float64Array(to - from) };
  if (n < 2) return found;
  const reach = REACH * spacing;
  const strength = STRENGTH * spacing * spacing;
  let x0 = Infinity;
  let y0 = Infinity;
  let z0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  let z1 = -Infinity;
  let biggest = 0;
  // A body whose place is not a number neither pushes nor is pushed (the layout puts it back):
  // left out of the tree, so the others still push each other.
  const placed: number[] = [];
  for (let i = 0; i < n; i++) {
    if (!Number.isFinite(x[i]! + y[i]! + z[i]! + r[i]!)) continue;
    placed.push(i);
    biggest = Math.max(biggest, r[i]!);
    x0 = Math.min(x0, x[i]!);
    y0 = Math.min(y0, y[i]!);
    z0 = Math.min(z0, z[i]!);
    x1 = Math.max(x1, x[i]!);
    y1 = Math.max(y1, y[i]!);
    z1 = Math.max(z1, z[i]!);
  }
  if (placed.length < 2) return found;
  const half = Math.max(x1 - x0, y1 - y0, z1 - z0) / 2 + 1;
  const root = build(placed, (x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2, half, 0, x, y, z, deep);
  // A node can overlap another one at most this far away: closer boxes are always opened.
  const overlap = 2 * biggest + 12;
  /** The boxes still to look at, for one node (reused). */
  const stack: Cell[] = [];

  for (let i = from; i < to; i++) {
    const px = x[i]!;
    const py = y[i]!;
    const pz = z[i]!;
    if (!Number.isFinite(px + py + pz + r[i]!)) continue;
    let fx = 0;
    let fy = 0;
    let fz = 0;
    stack.push(root);
    while (stack.length) {
      const cell = stack.pop()!;
      // How far the box's nearest and farthest points are.
      const ax = Math.abs(px - cell.cx);
      const ay = Math.abs(py - cell.cy);
      const az = Math.abs(pz - cell.cz);
      const nx = Math.max(ax - cell.half, 0);
      const ny = Math.max(ay - cell.half, 0);
      const nz = Math.max(az - cell.half, 0);
      if (nx * nx + ny * ny + nz * nz > reach * reach) continue;
      if (cell.items) {
        for (const j of cell.items) {
          if (j === i) continue;
          let dx = x[j]! - px;
          let dy = y[j]! - py;
          let dz = z[j]! - pz;
          let d2 = dx * dx + dy * dy + dz * dz;
          if (d2 < 0.01) {
            dx = Math.random() - 0.5;
            dy = Math.random() - 0.5;
            dz = deep ? Math.random() - 0.5 : 0;
            d2 = dx * dx + dy * dy + dz * dz + 0.01;
          }
          if (d2 > reach * reach) continue;
          const d = Math.sqrt(d2);
          let f = strength / d2;
          const min = r[i]! + r[j]! + 12;
          if (d < min) f += (min - d) * 0.08;
          fx -= (dx / d) * f;
          fy -= (dy / d) * f;
          fz -= (dz / d) * f;
        }
        continue;
      }
      const dx = cell.mx - px;
      const dy = cell.my - py;
      const dz = cell.mz - pz;
      const d2 = dx * dx + dy * dy + dz * dz;
      const size = cell.half * 2;
      const whole = size * size < THETA * THETA * d2 && nx * nx + ny * ny + nz * nz > overlap * overlap;
      if (whole) {
        if (d2 > reach * reach) continue;
        const d = Math.sqrt(d2);
        const f = (strength * cell.mass) / d2;
        fx -= (dx / d) * f;
        fy -= (dy / d) * f;
        fz -= (dz / d) * f;
      } else {
        for (const child of cell.children!) stack.push(child);
      }
    }
    // WebKit's optimising compiler (JavaScriptCore 2.54, the desktop app's engine) was seen
    // giving NaN here for finite places. One such push made the next step skip every push and
    // the nodes heaped up: a push that is not a number is worked out again plainly.
    if (!Number.isFinite(fx + fy + fz)) [fx, fy, fz] = plainPush(places, i, deep, reach, strength);
    found.fx[i - from] = fx;
    found.fy[i - from] = fy;
    found.fz[i - from] = fz;
  }
  return found;
}

/**
 * The push on body `i` from every other body within `reach`, one by one (no tree): the same
 * forces as {@link pushes}, for the rare body whose push came out not a number there. Two
 * bodies on the same spot push apart along x.
 */
export function plainPush(places: Places, i: number, deep: boolean, reach: number, strength: number): [number, number, number] {
  const { x, y, z, r } = places;
  let [fx, fy, fz] = [0, 0, 0];
  for (let j = 0; j < x.length; j++) {
    if (j === i || !Number.isFinite(x[j]! + y[j]! + z[j]! + r[j]!)) continue;
    let dx = x[j]! - x[i]!;
    const dy = y[j]! - y[i]!;
    const dz = deep ? z[j]! - z[i]! : 0;
    let d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < 0.01) {
      dx = j < i ? -0.1 : 0.1;
      d2 = dx * dx + dy * dy + dz * dz + 0.01;
    }
    if (!(d2 <= reach * reach)) continue;
    const d = Math.sqrt(d2);
    let f = strength / d2;
    const min = r[i]! + r[j]! + 12;
    if (d < min) f += (min - d) * 0.08;
    fx -= (dx / d) * f;
    fy -= (dy / d) * f;
    fz -= (dz / d) * f;
  }
  return Number.isFinite(fx + fy + fz) ? [fx, fy, fz] : [0, 0, 0];
}

/** The box at (`cx`, `cy`, `cz`), `half` wide each way, holding `items`, split as needed. */
function build(items: number[], cx: number, cy: number, cz: number, half: number, depth: number, x: Float64Array, y: Float64Array, z: Float64Array, deep: boolean): Cell {
  let mx = 0;
  let my = 0;
  let mz = 0;
  for (const i of items) {
    mx += x[i]!;
    my += y[i]!;
    mz += z[i]!;
  }
  const mass = items.length;
  const cell: Cell = { cx, cy, cz, half, mass, mx: mx / mass, my: my / mass, mz: mz / mass, children: null, items };
  if (items.length <= LEAF || depth >= MAX_DEPTH) return cell;
  // Split in 8 (in the plane every z is equal, so only 4 can fill).
  const parts: number[][] = Array.from({ length: deep ? 8 : 4 }, () => []);
  for (const i of items) parts[(x[i]! >= cx ? 1 : 0) + (y[i]! >= cy ? 2 : 0) + (deep && z[i]! >= cz ? 4 : 0)]!.push(i);
  const h = half / 2;
  cell.items = null;
  cell.children = parts.flatMap((part, k) => (part.length ? [build(part, cx + (k & 1 ? h : -h), cy + (k & 2 ? h : -h), deep ? cz + (k & 4 ? h : -h) : cz, h, depth + 1, x, y, z, deep)] : []));
  return cell;
}
